import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgModel,
} from "@angular/forms";

let nextInputId = 0;

@Component({
  selector: "app-cmn-input",
  templateUrl: "./cmn-input.component.html",
  styleUrls: ["./cmn-input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmnInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmnInputComponent implements ControlValueAccessor, AfterViewInit {
  private readonly generatedId = `cmn-input-${nextInputId++}`;
  private onTouched: () => void = () => { };
  private onChange: (value: string) => void = () => { };

  @Input() inputId = "";
  @Input() name = "";
  @Input() label = "";
  @Input() type: "email" | "number" | "search" | "tel" | "text" = "text";
  @Input({ transform: booleanAttribute }) required = false;
  @Input() maxlength: string | number | null = null;
  @Input() minlength: string | number | null = null;
  @Input() pattern: string | null = null;
  @Input() control: NgModel | null = null;
  @Input() showError = false;
  @Input({ transform: booleanAttribute }) readonly = false;
  @Input() requiredMessage = "";
  @Input() patternMessage = "";
  @Input() emailMessage = "";
  @Input() minlengthMessage = "";
  @Input() maxlengthReachedMessage = "";
  @Input({ transform: booleanAttribute }) showMaxlengthReached = false;
  @Input({ transform: booleanAttribute }) searchIcon = false;
  @Input({ transform: booleanAttribute }) loading = false;
  @Input() autocomplete: string | null = null;
  @Input() placeholder: string | null = null;
  @Input() min: string | number | null = null;
  @Input() inputmode: string | null = null;
  @Input() inputClass = "";
  @Input({ transform: booleanAttribute }) autofocus = false;
  @Input({ transform: booleanAttribute }) decimalOnly = false;

  @Output() valueChanged = new EventEmitter<Event>();
  @Output() change = new EventEmitter<Event>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();

  @ViewChild("inputElement")
  inputElement!: ElementRef<HTMLInputElement>;

  value = "";
  disabled = false;

  get id(): string {
    return this.inputId || this.generatedId;
  }

  get hasError(): boolean {
    return Boolean(this.control?.invalid && this.showError);
  }

  ngAfterViewInit(): void {
    if (this.autofocus) {
      window.setTimeout(() => {
        this.inputElement?.nativeElement.focus();
      }, 500);
    }
  }

  writeValue(value: string | number | null): void {
    this.value = value == null ? "" : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: Event): void {
    const input = event.target as HTMLInputElement;
  
    if (this.decimalOnly) {
      let sanitizedValue = input.value.replace(/[^0-9.]/g, "");
  
      const parts = sanitizedValue.split(".");
      if (parts.length > 2) {
        sanitizedValue = `${parts[0]}.${parts.slice(1).join("")}`;
      }
  
      const [integerPart, decimalPart] = sanitizedValue.split(".");
  
      if (decimalPart !== undefined) {
        sanitizedValue = `${integerPart}.${decimalPart.slice(0, 2)}`;
      }
  
      input.value = sanitizedValue;
    }
  
    this.value = input.value;
    this.onChange(this.value);
    this.valueChanged.emit(event);
  }

  handleBlur(event: FocusEvent): void {
    this.onTouched();
    this.inputBlur.emit(event);
  }

  handleChange(event: Event): void {
    this.change.emit(event);
  }
}
